/**
 * Bulk Upload — Vehicles/Drivers & Order Allocations
 *
 * Two-tab interface:
 *  1) Upload vehicles + drivers from spreadsheet (CSV parsed client-side)
 *  2) Upload truck-to-order allocations from spreadsheet
 *
 * Each tab has a "Download Template" button and a drag-and-drop upload zone.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  Download,
  FileSpreadsheet,
  Truck,
  Package,
  Check,
  X,
  AlertCircle,
  Loader,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import orderService from '../../../../services/orderService';

const mono = { fontFamily: 'IBM Plex Mono, monospace' };
const body = { fontFamily: 'DM Sans, sans-serif' };

/* ── CSV Parser ─────────────────────────────── */
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
        continue;
      }
      current += ch;
    }
    values.push(current.trim());
    const row = {};
    headers.forEach((h, i) => {
      row[h] = values[i] || '';
    });
    return row;
  });
}

/* ── Template generators ────────────────────── */
function downloadTemplate(type) {
  let csv, filename;
  if (type === 'vehicles') {
    csv = 'Reg No,Driver Name,Vehicle Type,License No,Contact,Haulier\n';
    csv += 'ABC 123 GP,John Smith,rigid,DL12345,+27 82 123 4567,ABC Haulage Ltd\n';
    csv += 'XYZ 456 NW,Jane Doe,interlink,DL67890,+27 83 987 6543,ABC Haulage Ltd\n';
    csv += ',Mike Williams,,DL11111,+27 84 555 1234,ABC Haulage Ltd\n';
    filename = 'vehicle_driver_template.csv';
  } else {
    csv = 'Order No,Reg No,Driver Name,Notes\n';
    csv += 'ORD-001,ABC 123 GP,John Smith,Morning delivery\n';
    csv += 'ORD-001,XYZ 456 NW,Jane Doe,Afternoon run\n';
    filename = 'order_allocation_template.csv';
  }
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Status Badge ───────────────────────────── */
const ResultBadge = ({ type, count, label }) => {
  const styles = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    error: 'bg-red-50 text-red-700 border-red-200',
    info: 'bg-sky-50 text-sky-700 border-sky-200',
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${styles[type]}`}>
      <span className="text-lg font-bold" style={mono}>
        {count}
      </span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
};

/* ── Upload Zone ────────────────────────────── */
const UploadZone = ({ onFileSelect, dragOver, setDragOver }) => {
  const inputRef = useRef(null);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) onFileSelect(file);
    },
    [onFileSelect, setDragOver]
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`
        relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200
        flex flex-col items-center justify-center py-12 px-6
        ${
          dragOver
            ? 'border-teal-400 bg-teal-50/60 scale-[1.01]'
            : 'border-slate-200 bg-slate-50/40 hover:border-slate-300 hover:bg-slate-50'
        }
      `}
    >
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
          dragOver ? 'bg-teal-100' : 'bg-slate-100'
        }`}
      >
        <Upload className={`w-6 h-6 ${dragOver ? 'text-teal-600' : 'text-slate-400'}`} />
      </div>
      <p className="text-sm font-semibold text-slate-700 mb-1">
        {dragOver ? 'Drop file here' : 'Drop your CSV file here'}
      </p>
      <p className="text-xs text-slate-400">or click to browse</p>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.txt"
        onChange={(e) => {
          if (e.target.files[0]) onFileSelect(e.target.files[0]);
          e.target.value = '';
        }}
        className="hidden"
      />
    </div>
  );
};

/* ── Preview Table ──────────────────────────── */
const PreviewTable = ({ rows, maxRows = 8 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!rows.length) return null;
  const headers = Object.keys(rows[0]);
  const display = expanded ? rows : rows.slice(0, maxRows);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest w-10">
                #
              </th>
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {display.map((row, i) => (
              <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-3 py-2 text-xs text-slate-300 font-medium" style={mono}>
                  {i + 1}
                </td>
                {headers.map((h) => (
                  <td
                    key={h}
                    className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap max-w-[180px] truncate"
                    style={mono}
                  >
                    {row[h] || <span className="text-slate-300">&mdash;</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > maxRows && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-2 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1 border-t border-slate-100 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" /> Show all {rows.length} rows
            </>
          )}
        </button>
      )}
    </div>
  );
};

/* ── Error List ─────────────────────────────── */
const ErrorList = ({ errors }) => {
  const [show, setShow] = useState(false);
  if (!errors?.length) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50/40 overflow-hidden">
      <button
        onClick={() => setShow(!show)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {errors.length} error{errors.length > 1 ? 's' : ''} found
        </div>
        {show ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {show && (
        <div className="border-t border-red-200 max-h-48 overflow-y-auto">
          {errors.map((err, i) => (
            <div
              key={i}
              className="px-4 py-2 text-xs text-red-600 border-b border-red-100 last:border-0 flex gap-2"
            >
              <span className="font-bold shrink-0" style={mono}>
                Row {err.row}:
              </span>
              <span>{err.error}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════ */
const BulkUpload = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vehicles');

  // Shared upload state
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = useCallback((f) => {
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      setRows(parsed);
    };
    reader.readAsText(f);
  }, []);

  const handleClear = () => {
    setFile(null);
    setRows([]);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!rows.length) return;
    setUploading(true);
    setResult(null);
    try {
      const res =
        activeTab === 'vehicles'
          ? await orderService.bulkUploadVehiclesDrivers(rows)
          : await orderService.bulkUploadOrderAllocations(rows);
      setResult(res.results);
    } catch (err) {
      setResult({ errors: [{ row: 0, error: err.response?.data?.error || err.message }] });
    } finally {
      setUploading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    handleClear();
  };

  const tabs = [
    {
      key: 'vehicles',
      label: 'Vehicles & Drivers',
      icon: Truck,
      description: 'Import fleet registry with driver assignments',
    },
    {
      key: 'allocations',
      label: 'Order Allocations',
      icon: Package,
      description: 'Assign trucks to orders in bulk',
    },
  ];

  return (
    <div style={body} className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Back nav */}
        <button
          onClick={() => navigate('/settings/modules/order-management/master-data')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4 group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          Master Data
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-11 h-11 bg-violet-600 rounded-lg flex items-center justify-center shadow-sm">
            <FileSpreadsheet size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Bulk Upload</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Import vehicles, drivers, and order allocations from spreadsheets
            </p>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => switchTab(tab.key)}
                className={`
                  relative p-4 rounded-xl border-2 text-left transition-all duration-200
                  ${
                    active
                      ? 'border-teal-500 bg-surface shadow-sm shadow-teal-100'
                      : 'border-slate-200 bg-surface hover:border-slate-300'
                  }
                `}
              >
                {active && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${
                    active ? 'bg-teal-50' : 'bg-slate-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-teal-600' : 'text-slate-400'}`} />
                </div>
                <p
                  className={`text-sm font-semibold ${active ? 'text-slate-900' : 'text-slate-600'}`}
                >
                  {tab.label}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{tab.description}</p>
              </button>
            );
          })}
        </div>

        {/* Template Download */}
        <div className="flex items-center justify-between bg-surface rounded-xl border border-slate-200 px-5 py-4 mb-5">
          <div>
            <p className="text-sm font-semibold text-slate-700">
              {activeTab === 'vehicles' ? 'Vehicle & Driver Template' : 'Order Allocation Template'}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {activeTab === 'vehicles'
                ? 'Columns: Reg No (optional), Driver Name, Vehicle Type, License No, Contact, Haulier'
                : 'Columns: Order No, Reg No, Driver Name, Notes'}
            </p>
          </div>
          <button
            onClick={() => downloadTemplate(activeTab)}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Download size={15} />
            Download CSV
          </button>
        </div>

        {/* Upload Zone / Preview */}
        {!file ? (
          <UploadZone
            onFileSelect={handleFileSelect}
            dragOver={dragOver}
            setDragOver={setDragOver}
          />
        ) : (
          <div className="space-y-4">
            {/* File info */}
            <div className="flex items-center justify-between bg-surface rounded-xl border border-slate-200 px-5 py-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{file.name}</p>
                  <p className="text-xs text-slate-400">
                    {rows.length} row{rows.length !== 1 ? 's' : ''} found
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleClear}
                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove file"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Preview */}
            {rows.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="w-4 h-4 text-slate-400" />
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Data Preview
                  </p>
                </div>
                <PreviewTable rows={rows} />
              </div>
            )}

            {/* Results */}
            {result && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Results
                </p>
                <div className="flex flex-wrap gap-3">
                  {activeTab === 'vehicles' ? (
                    <>
                      <ResultBadge
                        type="success"
                        count={result.created_vehicles || 0}
                        label="Vehicles created"
                      />
                      <ResultBadge
                        type="success"
                        count={result.created_drivers || 0}
                        label="Drivers created"
                      />
                      <ResultBadge type="info" count={result.linked || 0} label="Linked" />
                      {(result.skipped || 0) > 0 && (
                        <ResultBadge type="warning" count={result.skipped} label="Skipped" />
                      )}
                    </>
                  ) : (
                    <>
                      <ResultBadge type="success" count={result.allocated || 0} label="Allocated" />
                      {(result.skipped || 0) > 0 && (
                        <ResultBadge type="warning" count={result.skipped} label="Skipped" />
                      )}
                    </>
                  )}
                </div>
                <ErrorList errors={result.errors} />
              </div>
            )}

            {/* Upload button */}
            {rows.length > 0 && !result && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 shadow-sm"
              >
                {uploading ? (
                  <>
                    <Loader size={16} className="animate-spin" /> Processing {rows.length} rows...
                  </>
                ) : (
                  <>
                    <Upload size={16} /> Upload {rows.length} row{rows.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            )}

            {/* Upload more */}
            {result && (
              <button
                onClick={handleClear}
                className="w-full py-3 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-xl border border-slate-200 transition-colors"
              >
                Upload another file
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUpload;
